import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { personnelApi, pdvApi, uploadFile, importApi } from '../lib/api';
import { searchCities, loadComuni } from '../lib/comuni';
import { useAuth } from '../contexts/AuthContext';
import { useCurrentPdv } from '../contexts/CurrentPdvContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Users, Plus, Pencil, Trash2, ArrowRightLeft, AlertTriangle, Shield, Flame, Heart, HardHat, UserCheck, Upload, Download, FileSpreadsheet, Wand2 } from 'lucide-react';

// ========== CODICE FISCALE GENERATOR ==========
const MONTH_CODES = { 1: 'A', 2: 'B', 3: 'C', 4: 'D', 5: 'E', 6: 'H', 7: 'L', 8: 'M', 9: 'P', 10: 'R', 11: 'S', 12: 'T' };

const ODD_CHARS = {
  '0': 1, '1': 0, '2': 5, '3': 7, '4': 9, '5': 13, '6': 15, '7': 17, '8': 19, '9': 21,
  'A': 1, 'B': 0, 'C': 5, 'D': 7, 'E': 9, 'F': 13, 'G': 15, 'H': 17, 'I': 19, 'J': 21,
  'K': 2, 'L': 4, 'M': 18, 'N': 20, 'O': 11, 'P': 3, 'Q': 6, 'R': 8, 'S': 12, 'T': 14,
  'U': 16, 'V': 10, 'W': 22, 'X': 25, 'Y': 24, 'Z': 23
};

const EVEN_CHARS = {
  '0': 0, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9,
  'A': 0, 'B': 1, 'C': 2, 'D': 3, 'E': 4, 'F': 5, 'G': 6, 'H': 7, 'I': 8, 'J': 9,
  'K': 10, 'L': 11, 'M': 12, 'N': 13, 'O': 14, 'P': 15, 'Q': 16, 'R': 17, 'S': 18, 'T': 19,
  'U': 20, 'V': 21, 'W': 22, 'X': 23, 'Y': 24, 'Z': 25
};

// Codici catastali dei comuni italiani principali
const CODICI_CATASTALI = {
  // Sicilia
  'palermo': 'G273', 'catania': 'C351', 'messina': 'F158', 'siracusa': 'I754', 'ragusa': 'H163',
  'trapani': 'L331', 'agrigento': 'A089', 'caltanissetta': 'B429', 'enna': 'C342', 'marsala': 'E974',
  'gela': 'D960', 'vittoria': 'M088', 'modica': 'F258', 'acireale': 'A028', 'bagheria': 'A546',
  'alcamo': 'A176', 'mazara del vallo': 'F061', 'milazzo': 'F206', 'barcellona pozzo di gotto': 'A638',
  'villafranca tirrena': 'L950', 'taormina': 'L042', 'cefalu': 'C421', 'patti': 'G377',
  'terme vigliatore': 'L137', 'capo d\'orlando': 'B666', 'bovalino': 'B099', 'portorosa': 'G273',
  'san filippo del mela': 'H842', 'furnari': 'D825',
  // Calabria
  'reggio calabria': 'H224', 'catanzaro': 'C352', 'cosenza': 'D086', 'crotone': 'D122', 'vibo valentia': 'F537',
  'lamezia terme': 'M208',
  // Campania
  'napoli': 'F839', 'salerno': 'H703', 'caserta': 'B963', 'avellino': 'A509', 'benevento': 'A783',
  'torre del greco': 'L259', 'giugliano in campania': 'E054', 'castellammare di stabia': 'C129', 'pozzuoli': 'G964',
  // Puglia
  'bari': 'A662', 'taranto': 'L049', 'foggia': 'D643', 'lecce': 'E506', 'brindisi': 'B180',
  'andria': 'A285', 'barletta': 'A669', 'trani': 'L328',
  // Lazio
  'roma': 'H501', 'latina': 'E472', 'frosinone': 'D810', 'viterbo': 'M082', 'rieti': 'H282',
  'guidonia montecelio': 'E263', 'fiumicino': 'M297', 'aprilia': 'A341', 'pomezia': 'G811',
  // Lombardia
  'milano': 'F205', 'brescia': 'B157', 'bergamo': 'A794', 'monza': 'F704', 'como': 'C933',
  'varese': 'L682', 'pavia': 'G388', 'cremona': 'D150', 'mantova': 'E897', 'lecco': 'E507',
  'lodi': 'E648', 'sondrio': 'I829',
  // Piemonte
  'torino': 'L219', 'novara': 'F952', 'alessandria': 'A182', 'asti': 'A479', 'cuneo': 'D205',
  'vercelli': 'L750', 'biella': 'A859', 'verbania': 'L746',
  // Veneto
  'venezia': 'L736', 'verona': 'L781', 'padova': 'G224', 'vicenza': 'L840', 'treviso': 'L407',
  'rovigo': 'H620', 'belluno': 'A757', 'mestre': 'L736',
  // Emilia-Romagna
  'bologna': 'A944', 'modena': 'F257', 'parma': 'G337', 'reggio emilia': 'H223', 'ravenna': 'H199',
  'ferrara': 'D548', 'rimini': 'H294', 'forli': 'D704', 'cesena': 'C573', 'piacenza': 'G535',
  // Toscana
  'firenze': 'D612', 'prato': 'G999', 'livorno': 'E625', 'arezzo': 'A390', 'pisa': 'G702',
  'lucca': 'E715', 'pistoia': 'G713', 'massa': 'F023', 'carrara': 'B832', 'grosseto': 'E202', 'siena': 'I726',
  // Liguria
  'genova': 'D969', 'la spezia': 'E463', 'savona': 'I480', 'imperia': 'E290', 'sanremo': 'I138',
  // Marche
  'ancona': 'A271', 'pesaro': 'G479', 'fano': 'D488', 'ascoli piceno': 'A462', 'macerata': 'E783',
  // Umbria
  'perugia': 'G478', 'terni': 'L117', 'foligno': 'D653', 'citta di castello': 'C745',
  // Abruzzo
  'l\'aquila': 'A345', 'pescara': 'G482', 'teramo': 'L103', 'chieti': 'C632',
  // Sardegna
  'cagliari': 'B354', 'sassari': 'I452', 'quartu sant\'elena': 'H118', 'olbia': 'D522', 'nuoro': 'F979', 'oristano': 'G113',
  // Friuli-Venezia Giulia
  'trieste': 'L424', 'udine': 'L483', 'pordenone': 'G888', 'gorizia': 'E098',
  // Trentino-Alto Adige
  'trento': 'L378', 'bolzano': 'A952', 'merano': 'F132', 'rovereto': 'H612',
  // Basilicata
  'potenza': 'G942', 'matera': 'F052',
  // Molise
  'campobasso': 'B519', 'isernia': 'E335',
  // Valle d'Aosta
  'aosta': 'A326',
};

// Funzione per estrarre consonanti e vocali
const getConsonants = (str) => str.toUpperCase().replace(/[^BCDFGHJKLMNPQRSTVWXYZ]/g, '');
const getVowels = (str) => str.toUpperCase().replace(/[^AEIOU]/g, '');

// Genera le 3 lettere dal cognome
const getSurnameCode = (surname) => {
  const cleaned = surname.toUpperCase().replace(/[^A-Z]/g, '');
  const consonants = getConsonants(cleaned);
  const vowels = getVowels(cleaned);
  let code = consonants + vowels + 'XXX';
  return code.substring(0, 3);
};

// Genera le 3 lettere dal nome
const getNameCode = (name) => {
  const cleaned = name.toUpperCase().replace(/[^A-Z]/g, '');
  const consonants = getConsonants(cleaned);
  const vowels = getVowels(cleaned);
  
  if (consonants.length >= 4) {
    // Se ci sono 4+ consonanti, prendi la 1a, 3a e 4a
    return consonants[0] + consonants[2] + consonants[3];
  } else {
    let code = consonants + vowels + 'XXX';
    return code.substring(0, 3);
  }
};

// Genera il carattere di controllo
const getCheckChar = (code) => {
  let sum = 0;
  for (let i = 0; i < 15; i++) {
    const char = code[i];
    if (i % 2 === 0) {
      sum += ODD_CHARS[char] || 0;
    } else {
      sum += EVEN_CHARS[char] || 0;
    }
  }
  return String.fromCharCode(65 + (sum % 26));
};

// Genera il codice fiscale completo
const generateCodiceFiscale = (surname, name, birthDate, birthPlace, gender = 'M') => {
  if (!surname || !name || !birthDate || !birthPlace) {
    return '';
  }
  
  try {
    // Codice cognome (3 lettere)
    const surnameCode = getSurnameCode(surname);
    
    // Codice nome (3 lettere)
    const nameCode = getNameCode(name);
    
    // Data di nascita
    const date = new Date(birthDate);
    const year = date.getFullYear().toString().slice(-2);
    const month = MONTH_CODES[date.getMonth() + 1];
    let day = date.getDate();
    // Per le donne si aggiunge 40 al giorno
    if (gender === 'F') {
      day += 40;
    }
    const dayStr = day.toString().padStart(2, '0');
    
    // Codice catastale del comune
    const normalizedPlace = birthPlace.toLowerCase().trim();
    const comuneCode = CODICI_CATASTALI[normalizedPlace] || 'Z999'; // Z999 = estero/sconosciuto
    
    // Costruisci il codice senza carattere di controllo
    const partialCode = surnameCode + nameCode + year + month + dayStr + comuneCode;
    
    // Calcola il carattere di controllo
    const checkChar = getCheckChar(partialCode);
    
    return partialCode + checkChar;
  } catch (error) {
    console.error('Errore generazione CF:', error);
    return '';
  }
};

// ========== FINE CODICE FISCALE GENERATOR ==========

// ========== CITTÀ -> PROVINCIA + CAP ==========
const CITY_DATA = {
  // Sicilia
  'palermo': { province: 'PA', cap: '90100', region: 'Sicilia' },
  'catania': { province: 'CT', cap: '95100', region: 'Sicilia' },
  'messina': { province: 'ME', cap: '98100', region: 'Sicilia' },
  'siracusa': { province: 'SR', cap: '96100', region: 'Sicilia' },
  'ragusa': { province: 'RG', cap: '97100', region: 'Sicilia' },
  'trapani': { province: 'TP', cap: '91100', region: 'Sicilia' },
  'agrigento': { province: 'AG', cap: '92100', region: 'Sicilia' },
  'caltanissetta': { province: 'CL', cap: '93100', region: 'Sicilia' },
  'enna': { province: 'EN', cap: '94100', region: 'Sicilia' },
  'marsala': { province: 'TP', cap: '91025', region: 'Sicilia' },
  'gela': { province: 'CL', cap: '93012', region: 'Sicilia' },
  'vittoria': { province: 'RG', cap: '97019', region: 'Sicilia' },
  'modica': { province: 'RG', cap: '97015', region: 'Sicilia' },
  'acireale': { province: 'CT', cap: '95024', region: 'Sicilia' },
  'bagheria': { province: 'PA', cap: '90011', region: 'Sicilia' },
  'milazzo': { province: 'ME', cap: '98057', region: 'Sicilia' },
  'barcellona pozzo di gotto': { province: 'ME', cap: '98051', region: 'Sicilia' },
  'villafranca tirrena': { province: 'ME', cap: '98049', region: 'Sicilia' },
  'taormina': { province: 'ME', cap: '98039', region: 'Sicilia' },
  'patti': { province: 'ME', cap: '98066', region: 'Sicilia' },
  'terme vigliatore': { province: 'ME', cap: '98050', region: 'Sicilia' },
  'furnari': { province: 'ME', cap: '98054', region: 'Sicilia' },
  'capo d\'orlando': { province: 'ME', cap: '98071', region: 'Sicilia' },
  'piraino': { province: 'ME', cap: '98060', region: 'Sicilia' },
  // Calabria
  'reggio calabria': { province: 'RC', cap: '89100', region: 'Calabria' },
  'catanzaro': { province: 'CZ', cap: '88100', region: 'Calabria' },
  'cosenza': { province: 'CS', cap: '87100', region: 'Calabria' },
  'crotone': { province: 'KR', cap: '88900', region: 'Calabria' },
  'vibo valentia': { province: 'VV', cap: '89900', region: 'Calabria' },
  'lamezia terme': { province: 'CZ', cap: '88046', region: 'Calabria' },
  // Campania
  'napoli': { province: 'NA', cap: '80100', region: 'Campania' },
  'salerno': { province: 'SA', cap: '84100', region: 'Campania' },
  'caserta': { province: 'CE', cap: '81100', region: 'Campania' },
  'avellino': { province: 'AV', cap: '83100', region: 'Campania' },
  'benevento': { province: 'BN', cap: '82100', region: 'Campania' },
  // Puglia
  'bari': { province: 'BA', cap: '70100', region: 'Puglia' },
  'taranto': { province: 'TA', cap: '74100', region: 'Puglia' },
  'foggia': { province: 'FG', cap: '71100', region: 'Puglia' },
  'lecce': { province: 'LE', cap: '73100', region: 'Puglia' },
  'brindisi': { province: 'BR', cap: '72100', region: 'Puglia' },
  // Lazio
  'roma': { province: 'RM', cap: '00100', region: 'Lazio' },
  'latina': { province: 'LT', cap: '04100', region: 'Lazio' },
  'frosinone': { province: 'FR', cap: '03100', region: 'Lazio' },
  'viterbo': { province: 'VT', cap: '01100', region: 'Lazio' },
  'rieti': { province: 'RI', cap: '02100', region: 'Lazio' },
  // Lombardia
  'milano': { province: 'MI', cap: '20100', region: 'Lombardia' },
  'brescia': { province: 'BS', cap: '25100', region: 'Lombardia' },
  'bergamo': { province: 'BG', cap: '24100', region: 'Lombardia' },
  'monza': { province: 'MB', cap: '20900', region: 'Lombardia' },
  'como': { province: 'CO', cap: '22100', region: 'Lombardia' },
  'varese': { province: 'VA', cap: '21100', region: 'Lombardia' },
  'pavia': { province: 'PV', cap: '27100', region: 'Lombardia' },
  'cremona': { province: 'CR', cap: '26100', region: 'Lombardia' },
  'mantova': { province: 'MN', cap: '46100', region: 'Lombardia' },
  // Piemonte
  'torino': { province: 'TO', cap: '10100', region: 'Piemonte' },
  'novara': { province: 'NO', cap: '28100', region: 'Piemonte' },
  'alessandria': { province: 'AL', cap: '15100', region: 'Piemonte' },
  'asti': { province: 'AT', cap: '14100', region: 'Piemonte' },
  'cuneo': { province: 'CN', cap: '12100', region: 'Piemonte' },
  // Veneto
  'venezia': { province: 'VE', cap: '30100', region: 'Veneto' },
  'verona': { province: 'VR', cap: '37100', region: 'Veneto' },
  'padova': { province: 'PD', cap: '35100', region: 'Veneto' },
  'vicenza': { province: 'VI', cap: '36100', region: 'Veneto' },
  'treviso': { province: 'TV', cap: '31100', region: 'Veneto' },
  // Emilia-Romagna
  'bologna': { province: 'BO', cap: '40100', region: 'Emilia-Romagna' },
  'modena': { province: 'MO', cap: '41100', region: 'Emilia-Romagna' },
  'parma': { province: 'PR', cap: '43100', region: 'Emilia-Romagna' },
  'reggio emilia': { province: 'RE', cap: '42100', region: 'Emilia-Romagna' },
  'ravenna': { province: 'RA', cap: '48100', region: 'Emilia-Romagna' },
  'ferrara': { province: 'FE', cap: '44100', region: 'Emilia-Romagna' },
  'rimini': { province: 'RN', cap: '47900', region: 'Emilia-Romagna' },
  // Toscana
  'firenze': { province: 'FI', cap: '50100', region: 'Toscana' },
  'prato': { province: 'PO', cap: '59100', region: 'Toscana' },
  'livorno': { province: 'LI', cap: '57100', region: 'Toscana' },
  'arezzo': { province: 'AR', cap: '52100', region: 'Toscana' },
  'pisa': { province: 'PI', cap: '56100', region: 'Toscana' },
  'lucca': { province: 'LU', cap: '55100', region: 'Toscana' },
  'siena': { province: 'SI', cap: '53100', region: 'Toscana' },
  // Liguria
  'genova': { province: 'GE', cap: '16100', region: 'Liguria' },
  'la spezia': { province: 'SP', cap: '19100', region: 'Liguria' },
  'savona': { province: 'SV', cap: '17100', region: 'Liguria' },
  'imperia': { province: 'IM', cap: '18100', region: 'Liguria' },
  // Altre
  'ancona': { province: 'AN', cap: '60100', region: 'Marche' },
  'perugia': { province: 'PG', cap: '06100', region: 'Umbria' },
  'terni': { province: 'TR', cap: '05100', region: 'Umbria' },
  'l\'aquila': { province: 'AQ', cap: '67100', region: 'Abruzzo' },
  'pescara': { province: 'PE', cap: '65100', region: 'Abruzzo' },
  'cagliari': { province: 'CA', cap: '09100', region: 'Sardegna' },
  'sassari': { province: 'SS', cap: '07100', region: 'Sardegna' },
  'trieste': { province: 'TS', cap: '34100', region: 'Friuli-Venezia Giulia' },
  'udine': { province: 'UD', cap: '33100', region: 'Friuli-Venezia Giulia' },
  'trento': { province: 'TN', cap: '38100', region: 'Trentino-Alto Adige' },
  'bolzano': { province: 'BZ', cap: '39100', region: 'Trentino-Alto Adige' },
  'potenza': { province: 'PZ', cap: '85100', region: 'Basilicata' },
  'matera': { province: 'MT', cap: '75100', region: 'Basilicata' },
  'campobasso': { province: 'CB', cap: '86100', region: 'Molise' },
  'aosta': { province: 'AO', cap: '11100', region: "Valle d'Aosta" },
};

// Get city data (province, cap) from city name
const getCityData = (city) => {
  if (!city) return null;
  const normalized = city.toLowerCase().trim();
  // Exact match
  if (CITY_DATA[normalized]) return CITY_DATA[normalized];
  // Partial match
  for (const [key, data] of Object.entries(CITY_DATA)) {
    if (normalized.includes(key) || key.includes(normalized)) {
      return data;
    }
  }
  return null;
};
// ========== FINE CITTÀ -> PROVINCIA + CAP ==========

const safetyRoles = [
  { key: 'is_fire_safety_officer', label: 'Addetto Antincendio', icon: Flame, color: 'text-orange-500' },
  { key: 'is_first_aid_officer', label: 'Addetto Primo Soccorso', icon: Heart, color: 'text-red-500' },
  { key: 'is_emergency_officer', label: 'Addetto Emergenze', icon: Shield, color: 'text-blue-500' },
  { key: 'is_rls', label: 'RLS', icon: UserCheck, color: 'text-green-500' },
  { key: 'is_preposto', label: 'Preposto', icon: HardHat, color: 'text-amber-500' },
  { key: 'is_aspp', label: 'Addetto SPP', icon: Shield, color: 'text-purple-500' },
  { key: 'is_rspp_datore_lavoro', label: 'RSPP Datore di Lavoro', icon: HardHat, color: 'text-indigo-600' },
];

// Common job titles for dropdown
const JOB_TITLES = [
  // — Ristorazione / Bar / HACCP —
  'Cuoco',
  'Aiuto Cuoco',
  'Chef',
  'Pizzaiolo',
  'Pasticcere',
  'Fornaio',
  'Cameriere',
  'Cameriera ai piani',
  'Barista',
  'Banconiere',
  'Lavapiatti',
  'Addetto Banco Gastronomia',
  'Addetto Banco Macelleria',
  'Addetto Banco Pescheria',
  'Addetto Banco Panetteria',
  'Sommelier',
  // — Pulizie / Sanificazione —
  'Addetto Pulizie',
  'Addetto Sanificazione',
  // — Vendita / Retail / GDO —
  'Cassiere',
  'Addetto Vendita',
  'Capo Reparto',
  'Vice Capo Reparto',
  'Responsabile Punto Vendita',
  'Scaffalista',
  'Addetto Ortofrutta',
  // — Magazzino / Logistica —
  'Magazziniere',
  'Carrellista',
  'Facchino',
  'Mulettista',
  'Autista',
  'Fattorino / Rider',
  // — Edilizia / Manutenzione —
  'Muratore',
  'Carpentiere',
  'Manovale',
  'Ponteggiatore',
  'Saldatore',
  'Fabbro',
  'Carrozziere',
  'Elettricista',
  'Idraulico',
  'Termoidraulico',
  'Meccanico',
  'Manutentore',
  'Giardiniere',
  'Manutentore del verde',
  // — Ufficio / Amministrazione —
  'Impiegato',
  'Amministrativo',
  'Segretario/a',
  'Contabile',
  // — Sanitario / Estetico —
  'Estetista',
  'Parrucchiere',
  'Barbiere',
  'Infermiere',
  'OSS (Operatore Socio Sanitario)',
  'Operatore Sanitario',
  // — Generico —
  'Operaio Generico',
  'Apprendista',
  'Altro',
];

export default function PersonnelPage() {
  const { user } = useAuth();
  const { currentPdvId } = useCurrentPdv();
  const [personnel, setPersonnel] = useState([]);
  const [pdvList, setPdvList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [filterPdv, setFilterPdv] = useState(currentPdvId || 'all');
  const [transferTargetPdv, setTransferTargetPdv] = useState('');
  const [importPdvId, setImportPdvId] = useState(currentPdvId || '');
  const [importing, setImporting] = useState(false);
  const [birthPlaceOptions, setBirthPlaceOptions] = useState([]);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    pdv_id: '',
    first_name: '',
    last_name: '',
    gender: 'M',
    fiscal_code: '',
    birth_date: '',
    birth_place: '',
    birth_province: '',
    residence_address: '',
    residence_city: '',
    residence_province: '',
    residence_cap: '',
    email: '',
    phone: '',
    job_title: '',
    hire_date: '',
    contract_type: '',
    is_fire_safety_officer: false,
    is_first_aid_officer: false,
    is_emergency_officer: false,
    is_rls: false,
    is_preposto: false,
    is_aspp: false,
    is_rspp_datore_lavoro: false,
    has_training: false,
    last_training_date: '',
    training_location: '',
    training_notes: '',
    titolo_professionale: ''
  });

  // Auto-generate fiscal code when name, birth date and birth place change
  const handleGenerateCF = () => {
    const cf = generateCodiceFiscale(
      formData.last_name,
      formData.first_name,
      formData.birth_date,
      formData.birth_place,
      formData.gender
    );
    if (cf) {
      setFormData({ ...formData, fiscal_code: cf });
      toast.success('Codice Fiscale generato!');
    } else {
      toast.error('Compila Nome, Cognome, Data e Luogo di Nascita');
    }
  };

  useEffect(() => {
    loadData();
  }, [filterPdv]);

  // Sync local filterPdv with the global current UL whenever it changes
  useEffect(() => {
    if (currentPdvId && currentPdvId !== filterPdv) {
      setFilterPdv(currentPdvId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPdvId]);

  // Birth place autocomplete suggestions (from Italian municipalities)
  useEffect(() => {
    const term = (formData.birth_place || '').trim();
    if (term.length < 2) {
      setBirthPlaceOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await searchCities(term, 12);
        if (!cancelled) {
          const mapped = (r || []).map((c) => ({
            nome: c.nome || c.n,
            provincia: c.provincia || c.p,
            regione: c.regione || c.r,
          }));
          setBirthPlaceOptions(mapped);
        }
      } catch {
        if (!cancelled) setBirthPlaceOptions([]);
      }
    })();
    return () => { cancelled = true; };
  }, [formData.birth_place]);

  // Auto-refresh personnel list every 20s to reflect updates from other users (admin/azienda/UL)
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible' && !dialogOpen && !importDialogOpen && !transferDialogOpen) {
        loadData();
      }
    }, 20000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen, importDialogOpen, transferDialogOpen, filterPdv]);

  const loadData = async () => {
    try {
      const [personnelRes, pdvRes] = await Promise.all([
        personnelApi.getAll(filterPdv !== 'all' ? { pdv_id: filterPdv } : {}),
        pdvApi.getAll()
      ]);
      setPersonnel(personnelRes.data);
      setPdvList(pdvRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      pdv_id: filterPdv !== 'all' ? filterPdv : '',
      first_name: '',
      last_name: '',
      gender: 'M',
      fiscal_code: '',
      birth_date: '',
      birth_place: '',
      birth_province: '',
      residence_address: '',
      residence_city: '',
      residence_province: '',
      residence_cap: '',
      email: '',
      phone: '',
      job_title: '',
      hire_date: '',
      contract_type: '',
      is_fire_safety_officer: false,
      is_first_aid_officer: false,
      is_emergency_officer: false,
      is_rls: false,
      is_preposto: false,
      is_aspp: false,
      is_rspp_datore_lavoro: false,
      has_training: false,
      last_training_date: '',
      training_location: '',
      training_notes: '',
      titolo_professionale: ''
    });
    setSelectedPerson(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedPerson) {
        await personnelApi.update(selectedPerson.id, formData);
        toast.success('Dipendente aggiornato');
      } else {
        await personnelApi.create(formData);
        toast.success('Dipendente aggiunto');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleEdit = (person) => {
    setSelectedPerson(person);
    setFormData({
      pdv_id: person.pdv_id || '',
      first_name: person.first_name || '',
      last_name: person.last_name || '',
      fiscal_code: person.fiscal_code || '',
      gender: person.gender || 'M',
      birth_date: person.birth_date || '',
      birth_place: person.birth_place || '',
      birth_province: person.birth_province || '',
      residence_address: person.residence_address || '',
      residence_city: person.residence_city || '',
      residence_province: person.residence_province || '',
      residence_cap: person.residence_cap || '',
      email: person.email || '',
      phone: person.phone || '',
      job_title: person.job_title || '',
      hire_date: person.hire_date || '',
      contract_type: person.contract_type || '',
      is_fire_safety_officer: person.is_fire_safety_officer || false,
      is_first_aid_officer: person.is_first_aid_officer || false,
      is_emergency_officer: person.is_emergency_officer || false,
      is_rls: person.is_rls || false,
      is_preposto: person.is_preposto || false,
      is_aspp: person.is_aspp || false,
      is_rspp_datore_lavoro: person.is_rspp_datore_lavoro || false,
      has_training: person.has_training || false,
      last_training_date: person.last_training_date || '',
      training_location: person.training_location || '',
      training_notes: person.training_notes || '',
      titolo_professionale: person.titolo_professionale || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Rimuovere questo dipendente?')) return;
    try {
      await personnelApi.delete(id);
      toast.success('Dipendente rimosso');
      loadData();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleTransfer = async () => {
    if (!selectedPerson || !transferTargetPdv) return;
    try {
      await personnelApi.transfer(selectedPerson.id, transferTargetPdv);
      toast.success('Dipendente trasferito');
      setTransferDialogOpen(false);
      setSelectedPerson(null);
      setTransferTargetPdv('');
      loadData();
    } catch (error) {
      toast.error('Errore nel trasferimento');
    }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    try {
      const response = await importApi.importPersonnelExcel(file, importPdvId || null);
      const { imported, skipped, errors } = response.data;
      
      if (imported > 0) {
        toast.success(`Importati ${imported} dipendenti`);
      }
      if (skipped > 0) {
        toast.info(`${skipped} righe saltate (duplicati o dati mancanti)`);
      }
      if (errors && errors.length > 0) {
        toast.warning(`Alcuni errori: ${errors[0]}`);
      }
      
      setImportDialogOpen(false);
      setImportPdvId('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore importazione');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await importApi.downloadTemplate('personnel');
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_personale.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Errore download template');
    }
  };

  const getPdvName = (pdvId) => {
    const pdv = pdvList.find(p => p.id === pdvId);
    return pdv?.name || 'N/D';
  };

  const personnelWithoutTraining = personnel.filter(p => !p.has_training);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Alert for untrained personnel */}
      {personnelWithoutTraining.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3"
        >
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-amber-800">Attenzione: {personnelWithoutTraining.length} dipendenti senza formazione</p>
            <p className="text-sm text-amber-700">
              Alcuni dipendenti non hanno completato la formazione obbligatoria.
            </p>
          </div>
        </motion.div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl">Personale UL</h1>
          <p className="text-muted-foreground mt-1">Gestisci il personale delle Unità Locali</p>
        </div>
        <div className="flex gap-2">
          {/* Import Button */}
          <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="import-personnel-btn">
                <Upload className="w-4 h-4 mr-2" />
                Importa Excel
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Importa Personale da Excel</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Carica un file Excel con i dati del personale. Il file deve contenere colonne: Nome, Cognome, Codice Fiscale, Email, Telefono, Mansione.
                </p>
                
                <div className="space-y-2">
                  <Label>UL di destinazione (opzionale)</Label>
                  <Select value={importPdvId || "from_file"} onValueChange={(v) => setImportPdvId(v === "from_file" ? "" : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona UL o lascia vuoto" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="from_file">Usa colonna UL dal file</SelectItem>
                      {pdvList.map(pdv => (
                        <SelectItem key={pdv.id} value={pdv.id}>{pdv.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Se selezioni una UL, tutti i dipendenti saranno assegnati a quella UL. Altrimenti, il file deve contenere una colonna "UL" con i nomi delle unità locali.
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleDownloadTemplate} className="flex-1">
                    <Download className="w-4 h-4 mr-2" />
                    Scarica Template
                  </Button>
                </div>

                <div className="border-2 border-dashed rounded-lg p-6 text-center">
                  <FileSpreadsheet className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleImportExcel}
                    className="hidden"
                    id="personnel-file-input"
                  />
                  <Button
                    variant="secondary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={importing}
                  >
                    {importing ? 'Importazione...' : 'Seleziona File Excel'}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2">
                    Formati supportati: .xlsx, .xls
                  </p>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Add Button */}
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-personnel-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuovo Dipendente
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedPerson ? 'Modifica Dipendente' : 'Nuovo Dipendente'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* PDV e Mansione */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Unità Locale *</Label>
                  <Select value={formData.pdv_id} onValueChange={(v) => setFormData({...formData, pdv_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Seleziona UL" /></SelectTrigger>
                    <SelectContent>
                      {pdvList.map(pdv => (
                        <SelectItem key={pdv.id} value={pdv.id}>{pdv.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Mansione</Label>
                  <Input
                    list="job-titles-datalist"
                    value={formData.job_title || ''}
                    onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                    placeholder="Inizia a digitare o scegli dalla lista..."
                    data-testid="personnel-job-title-input"
                  />
                  <datalist id="job-titles-datalist">
                    {JOB_TITLES.map((title) => (
                      <option key={title} value={title} />
                    ))}
                  </datalist>
                  <p className="text-[10px] text-muted-foreground">
                    Suggerimenti: {JOB_TITLES.length} mansioni standard. Puoi anche digitarne una custom.
                  </p>
                </div>
              </div>
              
              {/* Dati Anagrafici */}
              <div className="p-4 border rounded-lg space-y-4">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase">Dati Anagrafici</h3>
                <div className="grid grid-cols-[120px_1fr_1fr] gap-4">
                  <div className="space-y-2">
                    <Label>Titolo</Label>
                    <Select
                      value={formData.titolo_professionale || '__none__'}
                      onValueChange={(v) => setFormData({...formData, titolo_professionale: v === '__none__' ? '' : v})}
                    >
                      <SelectTrigger data-testid="personnel-titolo-select"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">—</SelectItem>
                        {['Sig.','Sig.ra','Dott.','Dott.ssa','Ing.','Geom.','Arch.','P.I.','Dr.','Prof.','Rag.'].map(t => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Nome *</Label>
                    <Input value={formData.first_name} onChange={(e) => setFormData({...formData, first_name: e.target.value})} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Cognome *</Label>
                    <Input value={formData.last_name} onChange={(e) => setFormData({...formData, last_name: e.target.value})} required />
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Sesso</Label>
                    <Select value={formData.gender} onValueChange={(v) => setFormData({...formData, gender: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="M">Maschio</SelectItem>
                        <SelectItem value="F">Femmina</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Data di Nascita</Label>
                    <Input type="date" value={formData.birth_date} onChange={(e) => setFormData({...formData, birth_date: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Luogo di Nascita</Label>
                    <Input
                      list="birth-place-list"
                      value={formData.birth_place}
                      onChange={(e) => setFormData({ ...formData, birth_place: e.target.value })}
                      onFocus={() => loadComuni()}
                      placeholder="Inizia a digitare (es: Roma)"
                      autoComplete="off"
                      data-testid="birth-place-input"
                    />
                    <datalist id="birth-place-list">
                      {birthPlaceOptions.map((c) => (
                        <option key={c.nome + c.provincia} value={c.nome}>{c.nome} ({c.provincia})</option>
                      ))}
                    </datalist>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Codice Fiscale</Label>
                    <div className="flex gap-2">
                      <Input 
                        value={formData.fiscal_code} 
                        onChange={(e) => setFormData({...formData, fiscal_code: e.target.value.toUpperCase()})} 
                        maxLength={16} 
                        placeholder="RSSMRA80A01H501U" 
                        className="flex-1"
                      />
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={handleGenerateCF}
                        title="Genera automaticamente il Codice Fiscale"
                      >
                        <Wand2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">Compila Nome, Cognome, Sesso, Data e Luogo di Nascita poi clicca la bacchetta per generare</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Provincia di Nascita</Label>
                    <Input value={formData.birth_province} onChange={(e) => setFormData({...formData, birth_province: e.target.value.toUpperCase()})} maxLength={2} placeholder="RM" />
                  </div>
                </div>
              </div>
              
              {/* Residenza */}
              <div className="p-4 border rounded-lg space-y-4">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase">Residenza</h3>
                <div className="space-y-2">
                  <Label>Indirizzo</Label>
                  <Input value={formData.residence_address} onChange={(e) => setFormData({...formData, residence_address: e.target.value})} placeholder="Via Roma, 1" />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Città</Label>
                    <Input 
                      value={formData.residence_city} 
                      onChange={(e) => {
                        const newCity = e.target.value;
                        const cityData = getCityData(newCity);
                        setFormData({
                          ...formData, 
                          residence_city: newCity,
                          residence_province: cityData?.province || formData.residence_province,
                          residence_cap: cityData?.cap || formData.residence_cap
                        });
                      }} 
                      placeholder="Roma" 
                    />
                    {formData.residence_city && getCityData(formData.residence_city) && (
                      <p className="text-xs text-emerald-600">✓ Dati rilevati</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Provincia</Label>
                    <Input value={formData.residence_province} onChange={(e) => setFormData({...formData, residence_province: e.target.value.toUpperCase()})} maxLength={2} placeholder="RM" />
                  </div>
                  <div className="space-y-2">
                    <Label>CAP</Label>
                    <Input value={formData.residence_cap} onChange={(e) => setFormData({...formData, residence_cap: e.target.value})} maxLength={5} placeholder="00100" />
                  </div>
                </div>
              </div>
              
              {/* Contatti e Contratto */}
              <div className="p-4 border rounded-lg space-y-4">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase">Contatti e Contratto</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="mario.rossi@email.it" />
                  </div>
                  <div className="space-y-2">
                    <Label>Telefono</Label>
                    <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="+39 333 1234567" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Data Assunzione</Label>
                    <Input type="date" value={formData.hire_date} onChange={(e) => setFormData({...formData, hire_date: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo Contratto</Label>
                    <Select value={formData.contract_type} onValueChange={(v) => setFormData({...formData, contract_type: v})}>
                      <SelectTrigger><SelectValue placeholder="Seleziona tipo" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="indeterminato">Tempo Indeterminato</SelectItem>
                        <SelectItem value="determinato">Tempo Determinato</SelectItem>
                        <SelectItem value="apprendistato">Apprendistato</SelectItem>
                        <SelectItem value="part_time">Part-Time</SelectItem>
                        <SelectItem value="stage">Stage</SelectItem>
                        <SelectItem value="collaborazione">Collaborazione</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              
              {/* Safety Roles */}
              <div className="space-y-3">
                <Label>Ruoli di Sicurezza</Label>
                <div className="grid grid-cols-2 gap-3 p-4 bg-muted rounded-lg">
                  {safetyRoles.map(role => (
                    <div key={role.key} className="flex items-center space-x-2">
                      <Checkbox
                        id={role.key}
                        checked={formData[role.key]}
                        onCheckedChange={(checked) => setFormData({...formData, [role.key]: checked})}
                      />
                      <label htmlFor={role.key} className="flex items-center gap-2 text-sm cursor-pointer">
                        <role.icon className={`w-4 h-4 ${role.color}`} />
                        {role.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Training Section */}
              <div className="space-y-3 p-4 border rounded-lg">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="has_training"
                    checked={formData.has_training}
                    onCheckedChange={(checked) => setFormData({...formData, has_training: checked})}
                  />
                  <label htmlFor="has_training" className="font-medium cursor-pointer">Ha completato la formazione</label>
                </div>
                
                {formData.has_training && (
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div className="space-y-2">
                      <Label>Data Corso</Label>
                      <Input type="date" value={formData.last_training_date} onChange={(e) => setFormData({...formData, last_training_date: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Luogo Corso</Label>
                      <Input value={formData.training_location} onChange={(e) => setFormData({...formData, training_location: e.target.value})} />
                    </div>
                  </div>
                )}
              </div>
              
              <Button type="submit" className="w-full" disabled={!formData.pdv_id}>
                {selectedPerson ? 'Salva Modifiche' : 'Aggiungi Dipendente'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-4">
        <Select value={filterPdv} onValueChange={setFilterPdv}>
          <SelectTrigger className="w-[250px]">
            <SelectValue placeholder="Filtra per UL" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le UL</SelectItem>
            {pdvList.map(pdv => (
              <SelectItem key={pdv.id} value={pdv.id}>{pdv.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-6"><div className="flex justify-between"><div><p className="text-sm text-muted-foreground">Totale Dipendenti</p><p className="text-2xl font-bold">{personnel.length}</p></div><Users className="w-8 h-8 text-primary" /></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex justify-between"><div><p className="text-sm text-muted-foreground">Con Formazione</p><p className="text-2xl font-bold text-green-600">{personnel.filter(p => p.has_training).length}</p></div><UserCheck className="w-8 h-8 text-green-600" /></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex justify-between"><div><p className="text-sm text-muted-foreground">Senza Formazione</p><p className="text-2xl font-bold text-amber-600">{personnelWithoutTraining.length}</p></div><AlertTriangle className="w-8 h-8 text-amber-600" /></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex justify-between"><div><p className="text-sm text-muted-foreground">Addetti Sicurezza</p><p className="text-2xl font-bold text-blue-600">{personnel.filter(p => p.is_fire_safety_officer || p.is_first_aid_officer || p.is_emergency_officer).length}</p></div><Shield className="w-8 h-8 text-blue-600" /></div></CardContent></Card>
      </div>

      {/* Personnel List */}
      {personnel.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold text-lg mb-2">Nessun dipendente</h3>
            <p className="text-muted-foreground">Aggiungi il primo dipendente per iniziare</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {personnel.map((person, index) => (
            <motion.div
              key={person.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
            >
              <Card className={!person.has_training ? 'border-amber-300 bg-amber-50/50' : ''}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center font-semibold text-primary">
                        {person.first_name?.[0]}{person.last_name?.[0]}
                      </div>
                      <div>
                        <h3 className="font-semibold">{person.first_name} {person.last_name}</h3>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          <span>{getPdvName(person.pdv_id)}</span>
                          {person.job_title && <span>• {person.job_title}</span>}
                          {person.fiscal_code && <span>• {person.fiscal_code}</span>}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {/* Safety badges */}
                      <div className="flex gap-1">
                        {safetyRoles.filter(r => person[r.key]).map(role => (
                          <div key={role.key} className="p-1.5 bg-background rounded-full" title={role.label}>
                            <role.icon className={`w-4 h-4 ${role.color}`} />
                          </div>
                        ))}
                      </div>
                      
                      {/* Training status */}
                      {!person.has_training && (
                        <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-800 rounded">
                          Senza formazione
                        </span>
                      )}
                      
                      {/* Actions */}
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedPerson(person); setTransferDialogOpen(true); }} title="Trasferisci">
                          <ArrowRightLeft className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(person)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(person.id)}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Transfer Dialog */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Trasferisci Dipendente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Trasferisci <strong>{selectedPerson?.first_name} {selectedPerson?.last_name}</strong> a un'altra UL
            </p>
            <div className="space-y-2">
              <Label>UL di Destinazione</Label>
              <Select value={transferTargetPdv} onValueChange={setTransferTargetPdv}>
                <SelectTrigger><SelectValue placeholder="Seleziona UL" /></SelectTrigger>
                <SelectContent>
                  {pdvList.filter(p => p.id !== selectedPerson?.pdv_id).map(pdv => (
                    <SelectItem key={pdv.id} value={pdv.id}>{pdv.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleTransfer} className="w-full" disabled={!transferTargetPdv}>
              <ArrowRightLeft className="w-4 h-4 mr-2" />
              Trasferisci
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
